import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useCardVisibilityStore, CardVisibilityMap } from '../../store/cardVisibilityStore';

const CARD_DEFINITIONS: { key: string; label: string; hasBackend: boolean }[] = [
  { key: 'document_processing', label: 'Document Processing', hasBackend: true },
  { key: 'tampering_detection', label: 'Tampering Detection', hasBackend: true },
  { key: 'email_processor', label: 'Email Processor', hasBackend: true },
  { key: 'tariff_classifier', label: 'Tariff Classifier', hasBackend: true },
  { key: 'export_control', label: 'Export Control', hasBackend: true },
  { key: 'cross_document', label: 'Cross-Document Combination', hasBackend: true },
  { key: 'magic_marker', label: 'Magic Marker', hasBackend: true },
  { key: 'compliance_engine', label: 'Compliance Engine', hasBackend: true },
  { key: 'customs_audit', label: 'Customs Audit', hasBackend: true },
  { key: 'document_classifier', label: 'Document Classifier', hasBackend: true },
  { key: 'pdf_security_scanner', label: 'PDF Security Scanner', hasBackend: true },
  { key: 'nlp_rules', label: 'NLP Rules Engine', hasBackend: true },
  { key: 'supplier_declarations', label: 'Supplier Declarations', hasBackend: true },
  { key: 'ai_learning', label: 'AI Learning Module', hasBackend: false },
  { key: 'business_intelligence', label: 'Business Intelligence', hasBackend: false },
  { key: 'conversational_ai', label: 'Conversational AI', hasBackend: false },
  { key: 'workflow_automation', label: 'Workflow Automation', hasBackend: false },
  { key: 'blockchain', label: 'Blockchain Integration', hasBackend: false },
  { key: 'multi_language', label: 'Multi-Language Support', hasBackend: false },
  { key: 'master_data', label: 'Master Data Management', hasBackend: false },
  { key: 'predictive_analytics', label: 'Predictive Analytics', hasBackend: false },
  { key: 'smart_contract', label: 'Smart Contract Generation', hasBackend: false },
  { key: 'intelligent_routing', label: 'Intelligent Routing', hasBackend: false },
  { key: 'supply_chain', label: 'Supply Chain Visibility', hasBackend: false },
  { key: 'document_quality', label: 'Document Quality Scoring', hasBackend: false },
  { key: 'version_control', label: 'Version Control', hasBackend: false },
  { key: 'admin_panel', label: 'Admin Panel', hasBackend: false },
];

/** Admin toggle of which dashboard/sidebar feature cards are visible to users. */
const FeatureCardVisibilityPanel: React.FC = () => {
  const { cards, saveCardVisibility, fetchCardVisibility } = useCardVisibilityStore();
  const [localCards, setLocalCards] = useState<CardVisibilityMap>({ ...cards });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCardVisibility();
  }, [fetchCardVisibility]);

  useEffect(() => {
    setLocalCards({ ...cards });
  }, [cards]);

  const handleToggle = (key: string) => {
    setLocalCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveCardVisibility(localCards);
      toast.success('Card visibility settings saved successfully');
    } catch (error) {
      toast.error('Failed to save card visibility settings');
    } finally {
      setIsSaving(false);
    }
  };

  const activeFeatures = CARD_DEFINITIONS.filter((c) => c.hasBackend);
  const comingSoonFeatures = CARD_DEFINITIONS.filter((c) => !c.hasBackend);
  const hasChanges = JSON.stringify(localCards) !== JSON.stringify(cards);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Dashboard Feature Cards</Typography>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving || !hasChanges}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Active Features (Backend Ready)
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={1}>
          {activeFeatures.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <FormControlLabel
                control={<Switch checked={localCards[card.key] ?? false} onChange={() => handleToggle(card.key)} color="primary" />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{card.label}</Typography>
                    <Chip label="Active" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Coming Soon (No Backend Yet)
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {comingSoonFeatures.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <FormControlLabel
                control={<Switch checked={localCards[card.key] ?? false} onChange={() => handleToggle(card.key)} color="warning" />}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{card.label}</Typography>
                    <Chip label="Coming Soon" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default FeatureCardVisibilityPanel;
